import React from 'react'
import { Aircraft } from '../../../types/intellijetnet'
import { AircraftOverride, TakePriceModifier } from '../../../types/database'
import { formatPrice, formatAskingPrice, formatDate } from '../../../utils/formatters'
import { PriceWithModifier } from '../../shared/PriceWithModifier'
import InlineAskPriceEditor from '../../inline-editors/InlineAskPriceEditor'
import InlineTakePriceEditor from '../../inline-editors/InlineTakePriceEditor'
import InlineDateEditor from '../../inline-editors/InlineDateEditor'
import type { EditingCell, EditingCellFieldName } from '../types'
import type { EffectiveAircraft } from '../../../hooks/useMarketTableData'

interface ForSalePricingCellsProps {
  aircraft: Aircraft
  effectiveAircraft: EffectiveAircraft
  isEditMode?: boolean
  cellBorderClass: string
  isOffMarket: boolean
  overrides: Map<number, AircraftOverride>
  editingCell: EditingCell | null
  hideDateListed: boolean
  hideJnAsk: boolean
  hideAskingPrice: boolean
  hideExternalColumns: boolean
  handleStartEdit: (aircraftId: number, fieldName: EditingCellFieldName) => void
  handleCancelEdit: () => void
  handleFieldSave: (aircraftId: number, fieldName: EditingCellFieldName, newValue: string | number | null) => Promise<void>
  handleTakePriceSave: (aircraftId: number, priceFieldName: 'take_price' | 'external_take_price', newValue: number | null, newModifier: TakePriceModifier | null) => Promise<void>
  handleAskPriceSave: (aircraftId: number, priceFieldName: 'asking_price' | 'external_asking_price', newValue: number | null, newMakeOffer: boolean | null) => Promise<void>
}

/**
 * Renders the for-sale specific pricing columns: Date Listed, JN Ask, Ask/Take, and External prices.
 */
export function ForSalePricingCells(props: ForSalePricingCellsProps) {
  const { aircraft, effectiveAircraft, isEditMode, cellBorderClass, isOffMarket, overrides, editingCell,
    hideDateListed, hideJnAsk, hideAskingPrice, hideExternalColumns,
    handleStartEdit, handleCancelEdit, handleFieldSave, handleTakePriceSave, handleAskPriceSave } = props

  const override = overrides.get(aircraft.aircraft_id)
  const ec = (field: EditingCellFieldName) => editingCell?.aircraftId === aircraft.aircraft_id && editingCell?.fieldName === field
  const editClass = (hasOvr: boolean) => `${hasOvr ? 'text-red-800 font-medium' : ''} ${isEditMode ? 'cursor-pointer hover:bg-green-50 rounded px-1 -mx-1' : ''}`
  const startEdit = (field: EditingCellFieldName) => isEditMode ? (e: React.MouseEvent) => { e.stopPropagation(); handleStartEdit(aircraft.aircraft_id, field); } : undefined

  return (
    <>
      {!hideDateListed && (
        <td className={`px-2 py-2 text-sm text-gray-900 text-center ${cellBorderClass}`}>
          {isOffMarket || effectiveAircraft.status === 'Off Market' ? (
            <div>Off Mkt</div>
          ) : (
            <div className={editClass(override?.date_listed != null)} onClick={startEdit('date_listed')}>
              {ec('date_listed') ? (
                <InlineDateEditor
                  value={override?.date_listed ?? effectiveAircraft.datelisted ?? null}
                  fieldName="date_listed"
                  onSave={(v) => handleFieldSave(aircraft.aircraft_id, 'date_listed', v)}
                  onCancel={handleCancelEdit}
                />
              ) : (
                <>
                  <div>{formatDate(effectiveAircraft.datelisted)}</div>
                  {effectiveAircraft.datelisted && (
                    <div className="text-gray-500 text-xs">
                      {Math.floor((new Date().getTime() - new Date(effectiveAircraft.datelisted).getTime()) / (1000 * 60 * 60 * 24))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </td>
      )}
      {!hideJnAsk && (
        <td className={`px-2 py-2 text-sm text-gray-900 text-center ${cellBorderClass}`}>
          {formatPrice(aircraft.asking)}
        </td>
      )}
      {!hideAskingPrice && (
        <td className={`px-2 py-2 text-sm text-gray-900 text-center ${cellBorderClass}`}>
          <div className={editClass(override?.asking_price != null || override?.actual_ask_make_offer != null)} onClick={startEdit('asking_price')}>
            {ec('asking_price') ? (
              <InlineAskPriceEditor value={override?.asking_price ?? null} makeOffer={override?.actual_ask_make_offer ?? null} fieldName="asking_price"
                onSave={(v, mo) => handleAskPriceSave(aircraft.aircraft_id, 'asking_price', v, mo)} onCancel={handleCancelEdit} />
            ) : override?.actual_ask_make_offer ? 'M.O.' : formatAskingPrice(effectiveAircraft.asking)}
          </div>
          <div className={editClass(override?.take_price != null || override?.take_price_modifier != null)} onClick={startEdit('take_price')}>
            {ec('take_price') ? (
              <InlineTakePriceEditor value={override?.take_price ?? null} modifier={override?.take_price_modifier ?? null} fieldName="take_price"
                onSave={(v, m) => handleTakePriceSave(aircraft.aircraft_id, 'take_price', v, m)} onCancel={handleCancelEdit} />
            ) : <PriceWithModifier value={effectiveAircraft.take_price} modifier={override?.take_price_modifier} />}
          </div>
        </td>
      )}
      {!hideExternalColumns && (
        <td className={`px-2 py-2 text-sm text-gray-900 text-center ${cellBorderClass}`}>
          <div className={editClass(override?.external_asking_price != null || override?.adj_ask_make_offer != null)} onClick={startEdit('external_asking_price')}>
            {ec('external_asking_price') ? (
              <InlineAskPriceEditor value={override?.external_asking_price ?? null} makeOffer={override?.adj_ask_make_offer ?? null} fieldName="external_asking_price"
                onSave={(v, mo) => handleAskPriceSave(aircraft.aircraft_id, 'external_asking_price', v, mo)} onCancel={handleCancelEdit} />
            ) : override?.adj_ask_make_offer ? 'M.O.' : formatAskingPrice(override?.external_asking_price ?? effectiveAircraft.asking)}
          </div>
          <div className={editClass(override?.external_take_price != null || override?.external_take_price_modifier != null)} onClick={startEdit('external_take_price')}>
            {ec('external_take_price') ? (
              <InlineTakePriceEditor value={override?.external_take_price ?? null} modifier={override?.external_take_price_modifier ?? null} fieldName="external_take_price"
                onSave={(v, m) => handleTakePriceSave(aircraft.aircraft_id, 'external_take_price', v, m)} onCancel={handleCancelEdit} />
            ) : <PriceWithModifier value={override?.external_take_price ?? effectiveAircraft.take_price} modifier={override?.external_take_price_modifier ?? override?.take_price_modifier} />}
          </div>
        </td>
      )}
    </>
  )
}

